import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'
import Popup from 'reactjs-popup'

import ImageWithLoader from '@/components/UiElements/ImageWithLoader'

type HelpTooltipPosition = 'top center' | 'bottom center' | 'right center' | 'left center'

const AUTO_POSITIONS: HelpTooltipPosition[] = ['top center', 'bottom center', 'right center', 'left center']

let closeActiveHelpTooltip: (() => void) | null = null

interface HelpTooltipProps {
  children: ReactElement
  help: ReactNode
  title?: ReactNode
  imageUrl?: string
  imageAlt?: string
}

const HelpTooltip = ({ children, help, title, imageUrl, imageAlt = 'Help illustration' }: HelpTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const closeTooltip = useCallback(() => {
    setIsOpen(false)
    if (closeActiveHelpTooltip === closeTooltip) {
      closeActiveHelpTooltip = null
    }
  }, [])

  const openTooltip = useCallback(() => {
    if (closeActiveHelpTooltip !== closeTooltip) {
      closeActiveHelpTooltip?.()
    }

    closeActiveHelpTooltip = closeTooltip
    setIsOpen(true)
  }, [closeTooltip])

  useEffect(() => () => {
    if (closeActiveHelpTooltip === closeTooltip) {
      closeActiveHelpTooltip = null
    }
  }, [closeTooltip])

  if (!help) return children

  return (
    <Popup
      trigger={children}
      open={isOpen}
      on="hover"
      onOpen={openTooltip}
      onClose={closeTooltip}
      position={AUTO_POSITIONS}
      mouseEnterDelay={1000}
      mouseLeaveDelay={120}
      keepTooltipInside
      repositionOnResize
      arrow={false}
      closeOnDocumentClick
      closeOnEscape
      contentStyle={{ width: '270px', maxWidth: 'calc(100vw - 24px)', zIndex: 1000 }}
    >
      <div className="w-full rounded-md border border-gray-200 bg-white p-md text-text-sm leading-relaxed text-gray-700 shadow-lg [&_a]:text-primary-500 [&_a]:underline">
        {title && <div className="mb-xs font-semibold text-gray-900">{title}</div>}

        {typeof help === 'string' ? <p className="whitespace-pre-line">{help}</p> : help}

        {imageUrl && (
          <div className="mt-sm w-full">
            <ImageWithLoader
              src={imageUrl}
              alt={imageAlt}
              className="!h-auto !w-full rounded-sm"
              style={{ width: '100%', height: 'auto' }}
              cache
            />
          </div>
        )}
      </div>
    </Popup>
  )
}

export default HelpTooltip
